import { useState, useMemo, useEffect } from "react";
import { Card, CardBody } from "reactstrap";
import ApiService from "../../services/apiService";
import { LucideSettings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LucideView } from "lucide-react";
// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY       = "#1e563d";
const PRIMARY_LIGHT = "rgba(30, 86, 61, 0.16)";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DeviceType =
  | "Valve Controller"
  | "Pump Controller"
  | "Pump Comtroller"  // backend typo
  | "Sensor";

export interface Instrument {
  id:                   number;
  deviceId:             number;
  deviceName:           string;
  deviceType:           string;
  instrumentName:       string;
  instrumentNumber:     number | null;
  dateTime:             string | null;
  alarmBit:             number | null;
  firmwareVersion:      number | null;
  battery_Percent:      number | null;
  temperature:          number | null;
  status:               number | null;
  highTemperatureAlarm: number | null;
  lowBatteryAlarm:      number | null;
  auto_Manual:          number | null;
  networkStatus:        string | null;
  // Valve specific
  doorStatus:           number | null;
  readSwitchHigh:       number | null;
  dI2:                  number | null;
  dI1Counter:           number | null;
  dI2Counter:           number | null;
  beaconStatus:         number | null;
  doorSignal:           number | null;
  waterMeterSignal:     number | null;
  // Pump specific
  p_SysHealthy:         number | null;
  p_CurrentFault:       number | null;
  p_PhaseFault:         number | null;
  p_Phase1:             number | null;
  p_Phase2:             number | null;
  p_Phase3:             number | null;
  p_OverCurrentBit:     number | null;
  p_UnderCurrentBit:    number | null;
  p_PowerStatus:        number | null;
  ctValue:              number | null;
  ackBit:               number | null;
  p_LocalRemote:        number | null;
  type:                 string | null;
  phase:                number | null;
  underCurrent:         number | null;
  overCurrent:          number | null;
  capacity:             number | null;
  // Sensor specific
  moistureBit:          number | null;
  dry:                  number | null;
  wet:                  number | null;
  dry_SetPoint:         number | null;
  wet_SetPoint:         number | null;
  moisturePercentage:   number | null;
  // Additional
  byPassPhase:          number | null;
  byPassUnderCurrent:   number | null;
  byPassOverCurrent:    number | null;
}

// ─── Filter key ───────────────────────────────────────────────────────────────

type FilterKey = "all" | "Valve Controller" | "Pump Controller" | "Sensor" | "alarm";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Normalise deviceType string — handles backend typo "Pump Comtroller" */
function resolveType(deviceType: string): "Valve Controller" | "Pump Controller" | "Sensor" | "unknown" {
  const t = deviceType?.toLowerCase() ?? "";
  if (t.includes("valve"))  return "Valve Controller";
  if (t.includes("pump"))   return "Pump Controller";
  if (t.includes("sensor")) return "Sensor";
  return "unknown";
}

/** status: 1 = ON (green), 0 = OFF (red) */
function statusMeta(status: number | null): { label: string; dotColor: string; textColor: string } {
  if (status === 1) return { label: "ON",  dotColor: "#639922", textColor: "#3B6D11" };
  if (status === 0) return { label: "OFF", dotColor: "#E24B4A", textColor: "#A32D2D" };
  return                   { label: "—",   dotColor: "#adb5bd", textColor: "#6c757d" };
}

/** auto_Manual: 1 = Auto, 0 = Manual */
function modeMeta(auto_Manual: number | null): { label: string; bg: string; color: string } {
  if (auto_Manual === 1) return { label: "Auto",   bg: "#E6F1FB", color: "#185FA5" };
  if (auto_Manual === 0) return { label: "Manual", bg: "#FAEEDA", color: "#854F0B" };
  return                         { label: "—",     bg: "#f1f1f1", color: "#6c757d" };
}

/** networkStatus string → text color */
function networkColor(ns: string | null): string {
  switch (ns?.toLowerCase()) {
    case "excellent": return "#174530";  // dark green
    case "good":      return "#3B6D11";  // green
    case "average":   return "#854F0B";  // amber/yellow
    case "poor":      return "#E24B4A";  // red
    case "no signal": return "#adb5bd";  // grey
    default:          return "#6c757d";
  }
}

/** networkStatus → number of filled bars (1–4) */
function networkBars(ns: string | null): number {
  switch (ns?.toLowerCase()) {
    case "excellent": return 4;
    case "good":      return 3;
    case "average":   return 2;
    case "poor":      return 1;
    default:          return 0;
  }
}

/** battery_Percent → color */
function batteryColor(pct: number | null): string {
  if (pct === null) return "#6c757d";
  if (pct >= 60)    return "#3B6D11";
  if (pct >= 30)    return "#854F0B";
  return                   "#dc3545";
}

/** "2026-05-18T12:34:15" → "18 May, 12:34" */
function formatDateTime(dt: string | null): string {
  if (!dt) return "—";
  const d = new Date(dt);
  return (
    d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) +
    ", " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

/** deviceType → badge meta */
function typeBadge(deviceType: string): { label: string; icon: string; bg: string; color: string } {
  const t = resolveType(deviceType);
  if (t === "Valve Controller") return { label: "Valve",  icon: "ti-adjustments-horizontal", bg: PRIMARY_LIGHT, color: PRIMARY   };
  if (t === "Pump Controller")  return { label: "Pump",   icon: "ti-engine",                 bg: "#E6F1FB",     color: "#185FA5" };
  if (t === "Sensor")           return { label: "Sensor", icon: "ti-radar",                  bg: "#E1F5EE",     color: "#0F6E56" };
  return                               { label: deviceType, icon: "ti-device-unknown",       bg: "#f1f1f1",     color: "#6c757d" };
}

// ─── NetworkSignal ────────────────────────────────────────────────────────────

const NetworkSignal: React.FC<{ ns: string | null }> = ({ ns }) => {
  const bars   = networkBars(ns);
  const color  = networkColor(ns);
  const heights = [4, 7, 10, 13];

  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2, marginRight: 4 }}>
      {heights.map((h, i) => (
        <span
          key={i}
          style={{
            display:      "inline-block",
            width:        3,
            height:       h,
            borderRadius: 1,
            background:   i < bars ? color : "#dee2e6",
          }}
        />
      ))}
    </span>
  );
};

// ─── BatteryBar ───────────────────────────────────────────────────────────────

const BatteryBar: React.FC<{ pct: number | null }> = ({ pct }) => {
  const color = batteryColor(pct);
  const fill  = pct !== null ? Math.max(0, Math.min(100, pct)) : 0;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      {/* Battery shell */}
      <span style={{
        display:      "inline-flex",
        alignItems:   "center",
        width:        36,
        height:       14,
        borderRadius: 3,
        border:       `1.5px solid ${color}`,
        padding:      2,
        position:     "relative",
        flexShrink:   0,
      }}>
        <span style={{
          width:        `${fill}%`,
          height:       "100%",
          borderRadius: 1,
          background:   color,
          transition:   "width 0.3s",
        }} />
        {/* Battery tip */}
        <span style={{
          position:     "absolute",
          right:        -5,
          top:          "50%",
          transform:    "translateY(-50%)",
          width:        3,
          height:       7,
          background:   color,
          borderRadius: "0 2px 2px 0",
        }} />
      </span>
      <span style={{ fontSize: 11, color, fontWeight: 500, minWidth: 30 }}>
        {pct !== null ? `${pct}%` : "—"}
      </span>
    </span>
  );
};

// ─── MoistureBar (for Sensor rows) ───────────────────────────────────────────

const MoistureBar: React.FC<{ pct: number | null }> = ({ pct }) => {
  const fill  = pct !== null ? Math.max(0, Math.min(100, pct)) : 0;
  const color = "#0F6E56";

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {/* Bar — same width as battery shell for alignment */}
      <span style={{
        display:      "inline-block",
        width:        52,
        height:       6,
        borderRadius: 3,
        background:   "#e9ecef",
        overflow:     "hidden",
        flexShrink:   0,
      }}>
        <span style={{
          display:      "block",
          width:        `${fill}%`,
          height:       "100%",
          background:   color,
          borderRadius: 3,
        }} />
      </span>
      <span style={{ fontSize: 11, color, fontWeight: 500, minWidth: 36 }}>
        {pct !== null ? `${pct}%` : "—"}
      </span>
    </span>
  );
};

// ─── InstrumentsSection ───────────────────────────────────────────────────────

interface InstrumentsSectionProps {
  onDetail?: (instrument: Instrument) => void;
  onManage?: () => void;
  userId?:   string | number;
}

/**
 * InstrumentsSection
 * ------------------
 * Fetches instruments by userId, shows a filterable summary table.
 * Columns: Name, Type, Status, Mode, Battery/Moisture, Temp, Network, Last seen, →
 *
 * Filter tabs: All | Valves | Pumps | Sensors | ⚠ Alarms
 *
 * Sensor rows show moisturePercentage instead of battery bar.
 * Click → button to open detail / history / timeline for that instrument.
 */
const InstrumentsSection: React.FC<InstrumentsSectionProps> = ({
  onDetail,
  onManage,
  userId,
}) => {
  const [activeFilter,   setActiveFilter]   = useState<FilterKey>("all");
  const [instrumentInfo, setInstrumentInfo] = useState<Instrument[]>([]);
  const [loading,        setLoading]        = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;
    setLoading(true);

    const getUserInstruments = async () => {
      try {
        const res = await ApiService.axiosApi(
          `/instrumentmaster/user/${userId}`,
          { method: "GET" }
        );
        if (res.success) {
          // ✅ Fix 1: use res.data directly — not res.data.data
          setInstrumentInfo(res.data.instruments || []);
        } else {
          console.warn("UserInstrument", res.data);
        }
      } catch (e) {
        console.error("UserInstrument", e);
      } finally {
        setLoading(false);
      }
    };
    getUserInstruments();
  }, [userId]);

  // ── Filter tab counts ──────────────────────────────────────────────────────
  const tabs = useMemo(() => [
    {
      key:   "all" as FilterKey,
      label: `All (${instrumentInfo.length})`,
    },
    {
      key:   "Valve Controller" as FilterKey,
      label: `Valves (${instrumentInfo.filter(i => resolveType(i.deviceType) === "Valve Controller").length})`,
    },
    {
      key:   "Pump Controller" as FilterKey,
      label: `Pumps (${instrumentInfo.filter(i => resolveType(i.deviceType) === "Pump Controller").length})`,
    },
    {
      key:   "Sensor" as FilterKey,
      label: `Sensors (${instrumentInfo.filter(i => resolveType(i.deviceType) === "Sensor").length})`,
    },
    {
      key:   "alarm" as FilterKey,
      label: `⚠ Alarms (${instrumentInfo.filter(i =>
        i.alarmBit === 1 || i.highTemperatureAlarm === 1 || i.lowBatteryAlarm === 1
      ).length})`,
    },
  ], [instrumentInfo]);

  // ── Filtered list ──────────────────────────────────────────────────────────
  // ✅ Fix 2: switch cases now match tab keys exactly
  const filtered = useMemo(() => {
    switch (activeFilter) {
      case "Valve Controller":
        return instrumentInfo.filter(i => resolveType(i.deviceType) === "Valve Controller");
      case "Pump Controller":
        return instrumentInfo.filter(i => resolveType(i.deviceType) === "Pump Controller");
      case "Sensor":
        return instrumentInfo.filter(i => resolveType(i.deviceType) === "Sensor");
      case "alarm":
        return instrumentInfo.filter(i =>
          i.alarmBit === 1 || i.highTemperatureAlarm === 1 || i.lowBatteryAlarm === 1
        );
      default:
        return instrumentInfo;
    }
  }, [instrumentInfo, activeFilter]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.wrapper}>

      {/* Section header */}
      <div style={styles.sectionHeader}>
        <p style={styles.sectionLabel}>INSTRUMENTS</p>
        {onManage && (
          <button style={styles.actionBtn} onClick={onManage}>
            Manage all →
          </button>
        )}
      </div>

      <Card style={styles.card}>

        {/* Card header */}
        <div style={styles.cardHeader}>
          <div style={styles.cardHeaderLeft}>
            <div style={styles.headerIcon} >
                 <LucideSettings width={20} height={20} />
            </div>
            <span style={styles.headerTitle}>All instruments</span>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={styles.filterBar}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              style={{
                ...styles.filterTab,
                ...(activeFilter === tab.key                          ? styles.filterTabActive     : {}),
                ...(tab.key === "alarm"                               ? styles.filterTabAlarm      : {}),
                ...(tab.key === "alarm" && activeFilter === "alarm"   ? styles.filterTabAlarmActive: {}),
              }}
              onClick={() => setActiveFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ✅ Fix 3: loading state rendered before table */}
        <CardBody style={{ padding: 0 }}>
          {loading ? (
            <p style={{ padding: 16, color: "#6c757d", margin: 0 }}>
              Loading instruments...
            </p>
          ) : filtered.length === 0 ? (
            <p style={{ padding: 16, color: "#6c757d", margin: 0 }}>
              No instruments found.
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    <th style={{ ...styles.th, width: 160 }}>Name</th>
                    <th style={{ ...styles.th, width: 90  }}>Type</th>
                    <th style={{ ...styles.th, width: 80  }}>Status</th>
                    <th style={{ ...styles.th, width: 90  }}>Mode</th>
                        <th style={{ ...styles.th, width: 140 }}>Battery</th>
                         <th style={{ ...styles.th, width: 140 }}>Moisture</th>
                    <th style={{ ...styles.th, width: 90  }}>Temp</th>
                    <th style={{ ...styles.th, width: 130 }}>Network</th>
                    <th style={{ ...styles.th, width: 130 }}>Last seen</th>
                    <th style={{ ...styles.th, width: 80  }}>Alarm</th>
                    <th style={{ ...styles.th, width: 44  }}>Details</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((inst) => {
                    const type     = typeBadge(inst.deviceType);
                    const status   = statusMeta(inst.status);
                    const mode     = modeMeta(inst.auto_Manual);
                    const isSensor = resolveType(inst.deviceType) === "Sensor";
                    const hasAlarm =
                      inst.alarmBit === 1 ||
                      inst.highTemperatureAlarm === 1 ||
                      inst.lowBatteryAlarm === 1;

                    return (
                      <tr key={inst.id} style={styles.tr}>

                        {/* Name — no alarm dot here anymore */}
                        <td style={styles.td}>
                          <div>
                            <div style={styles.instrName}>{inst.instrumentName}</div>

                          </div>
                        </td>

                        {/* Type badge */}
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, background: type.bg, color: type.color }}>
                            <i
                              className={`ti ${type.icon}`}
                              aria-hidden="true"
                              style={{ fontSize: 13, marginRight: 3 }}
                            />
                            {type.label}
                          </span>
                        </td>

                        {/* Status — sensors don't have on/off */}
                        <td style={styles.td}>
                          {isSensor ? (
                            <span style={styles.dash}>—</span>
                          ) : (
                            <div style={styles.statusRow}>
                              <span style={{ ...styles.dot, background: status.dotColor }} />
                              <span style={{ fontSize: 12, color: status.textColor, fontWeight: 500 }}>
                                {status.label}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Mode — sensors don't have auto/manual */}
                        <td style={styles.td}>
                          {isSensor ? (
                            <span style={styles.dash}>—</span>
                          ) : (
                            <span style={{ ...styles.badge, background: mode.bg, color: mode.color }}>
                              {mode.label}
                            </span>
                          )}
                        </td>

                        {/* Battery (Valve/Pump) OR Moisture (Sensor) */}
                        <td style={styles.td}>

                            <BatteryBar pct={inst.battery_Percent} />

                        </td>

                        <td style={styles.td}>
                          {isSensor ? (
                            <MoistureBar pct={inst.moisturePercentage} />
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Temperature */}
                        <td style={styles.td}>
                          <span style={{
                            fontSize:   12,
                            fontWeight: 500,
                            color:      inst.highTemperatureAlarm === 1 ? "#854F0B" : "#212529",
                          }}>
                            {inst.temperature !== null ? `${inst.temperature}°C` : <span style={styles.dash}>—</span>}
                            {inst.highTemperatureAlarm === 1 && (
                              <i
                                className="ti ti-flame"
                                aria-hidden="true"
                                style={{ fontSize: 11, color: "#854F0B", marginLeft: 3 }}
                              />
                            )}
                          </span>
                        </td>

                        {/* Network */}
                        <td style={styles.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <NetworkSignal ns={inst.networkStatus} />
                            <span style={{ fontSize: 13, color: networkColor(inst.networkStatus) }}>
                              {inst.networkStatus ?? "—"}
                            </span>
                          </div>
                        </td>

                        {/* Last seen */}
                        <td style={styles.td}>
                          <span style={styles.lastSeen}>
                            {formatDateTime(inst.dateTime)}
                          </span>
                        </td>

                        {/* Alarm column */}
                        <td style={styles.td}>
                          {hasAlarm ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={styles.alarmDot} />

                            </div>
                          ) : (
                            <span style={styles.dash}>—</span>
                          )}
                        </td>

                        {/* Detail → button */}
                        <td style={{ ...styles.td, textAlign: "right" as const }}>
                          <button
                            style={styles.detailBtn}
                            onClick={(e) => { e.stopPropagation(); navigate(`/instrument/detail`,{
                               state:{
                                  id:inst.id,
                                  deviceType: inst.deviceType,
                                  deviceName : inst.instrumentName
                               }
                            })  }}
                            title="View details"
                          >
                             <LucideView/>
                          </button>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  wrapper:       { marginBottom: "1.5rem" },
  sectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  sectionLabel:  { fontSize: 14, fontWeight: 500, color: "#6c757d", letterSpacing: "0.06em", margin: 0 },
  actionBtn:     { fontSize: 11, color: PRIMARY, background: "none", border: "none", cursor: "pointer", padding: 0 },

  card:           { border: "0.5px solid #dee2e6", borderRadius: 12, overflow: "hidden", boxShadow: "none" },
  cardHeader:     { padding: "10px 14px", borderBottom: "0.5px solid #dee2e6", display: "flex", alignItems: "center" },
  cardHeaderLeft: { display: "flex", alignItems: "center", gap: 8 },
  headerIcon:     { width: 30, height: 30, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor:PRIMARY_LIGHT },
  headerTitle:    { fontSize: 16, fontWeight: 500, color: "#212529" },

  filterBar:            { display: "flex", gap: 6, padding: "10px 14px", borderBottom: "0.5px solid #dee2e6", flexWrap: "wrap" as const },
  filterTab:            { fontSize: 12, padding: "4px 12px", borderRadius: 8, border: "0.5px solid #dee2e6", background: "none", color: "#6c757d", cursor: "pointer" },
  filterTabActive:      { background: PRIMARY, color: "#fff", borderColor: PRIMARY },
  filterTabAlarm:       { color: "#854F0B", borderColor: "#FAC775" },
  filterTabAlarmActive: { background: "#FAEEDA", color: "#854F0B", borderColor: "#FAC775" },

  table:     { width: "100%", borderCollapse: "collapse" as const },
  thead:     { background: "#EEF4F0" },
  th:        { fontSize: 14, fontWeight: 500, color: "#6c757d", padding: "9px 14px", textAlign: "left" as const, borderBottom: "0.5px solid #dee2e6", whiteSpace: "nowrap" as const },
  tr:        { borderBottom: "0.5px solid #dee2e6" },
  td:        { padding: "10px 14px", fontSize: 13, verticalAlign: "middle" as const, whiteSpace: "nowrap" as const },

  instrName:  { fontSize: 13, fontWeight: 500, color: "#212529" },
  instrSub:   { fontSize: 10, color: "#6c757d", marginTop: 1 },

  alarmDot: {
    width:      10,
    height:     10,
    borderRadius: "50%",
    background: "#E24B4A",
    flexShrink: 0,
    boxShadow:  "0 0 0 2px rgba(226,75,74,0.2)",
  },

  badge:     { display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 500, padding: "3px 8px", borderRadius: 8 },
  statusRow: { display: "flex", alignItems: "center", gap: 6 },
  dot:       { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  lastSeen:  { fontSize: 13, color: "#212529" },
  dash:      { fontSize: 13, color: "#212529" },

  detailBtn: {
    width:          28,
    height:         28,
    borderRadius:   7,
    border:         "0.5px solid rgb(202, 206, 209)",
    background:     "#f8f9fa",
    display:        "flex",

    cursor:         "pointer",
    color:          "#6c757d",
  },
};

export default InstrumentsSection;
